import React from "react";
import { Bookmark, Play, X } from "lucide-react";

/**
 * "My playlist" panel — lists the user's saved talks. Self-hides when empty.
 *
 * Each item:
 *  - Title + speaker + day badge
 *  - Click anywhere on the card → open in the talk modal (if video available)
 *  - X button → remove from playlist
 */
export default function MyPlaylistPanel({ items, onOpenTalk, onRemove, busyId }) {
  if (!items || items.length === 0) return null;

  return (
    <section
      id="my-playlist"
      className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-8"
      data-testid="my-playlist-panel"
    >
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-2xl bg-[#28BBE3]/15 text-[#28BBE3] ring-1 ring-[#28BBE3]/30 flex items-center justify-center shrink-0">
          <Bookmark size={18} strokeWidth={1.8} />
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Your saves</div>
          <h3 className="mt-1 font-heading text-xl text-slate-900 font-medium tracking-tight">My playlist</h3>
        </div>
      </div>

      <div
        className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
        data-testid="my-playlist-list"
      >
        {items.slice(0, 9).map((s) => (
          <div
            key={s.session_id}
            className="group relative rounded-2xl ring-1 ring-black/5 p-4 hover:ring-[#28BBE3]/40 hover:bg-[#28BBE3]/[0.03] transition-colors"
            data-testid={`playlist-card-${s.session_id}`}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRemove?.(s.session_id); }}
              disabled={busyId === s.session_id}
              className="absolute top-2.5 right-2.5 h-6 w-6 rounded-full bg-white ring-1 ring-black/5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition-colors"
              data-testid={`playlist-remove-${s.session_id}`}
              title="Remove from My playlist"
              aria-label="Remove from My playlist"
            >
              <X size={12} strokeWidth={2.4} />
            </button>

            <button
              type="button"
              onClick={() => onOpenTalk?.(s)}
              disabled={!s.video_url}
              className="w-full text-left disabled:cursor-default"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                <span>{s.day_label ? prettyDayShort(s.day_label) : ""}</span>
                {s.is_editors_pick && (
                  <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 text-[10px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5">
                    Pick
                  </span>
                )}
              </div>
              <div className="mt-1.5 text-sm font-medium text-slate-900 line-clamp-2 leading-snug pr-6">
                {s.title}
              </div>
              {s.speaker && (
                <div className="text-xs text-slate-500 mt-1 truncate">{s.speaker}</div>
              )}
              {s.video_url ? (
                <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-[#28BBE3]">
                  <Play size={10} strokeWidth={2.6} /> Watch
                </div>
              ) : (
                <div className="mt-3 inline-flex text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
                  Video coming soon
                </div>
              )}
            </button>
          </div>
        ))}
      </div>

      {items.length > 9 && (
        <p className="mt-4 text-[11px] text-slate-400">
          +{items.length - 9} more saved talks
        </p>
      )}
    </section>
  );
}

function prettyDayShort(label) {
  // "Day 1 - Monday 22 June 2026" → "Mon 22 Jun"
  if (!label) return "";
  const m = label.match(/-\s*(\w+)\s+(\d+)\s+(\w+)/i);
  if (m) {
    return `${m[1].slice(0, 3)} ${m[2]} ${m[3].slice(0, 3)}`;
  }
  return label;
}
